/**
 * Masterclass Context Provider
 *
 * Manages the global state of the masterclass application including:
 * - User progression through cases (visited slides, answers, scores)
 * - Selected case
 * - Video states (intro, confirmation, completion)
 * - User profile
 *
 * Synchronization with Supabase via React Query:
 * - Loads progression on mount and auth changes
 * - Auto-saves progress changes using mutations
 * - Automatic migration from old data format (indices → IDs)
 *
 * @example
 * ```tsx
 * const { selectedCase, updateCaseProgress, caseProgress } = useMasterclass();
 * updateCaseProgress(1, 'slide-intro');
 * ```
 */
import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from 'react';

import { GetMasterClassUserSessionDocument } from '@/@generated/codegen/API';
import {
  useAuthUser,
  useAnswers,
  useProfile,
  useUpsertCaseProgress,
  useInsertAnswer,
  useDeleteAnswers,
  useResetCaseProgress,
  useUpsertCaseStatistics,
} from '@/hooks/queries';
import { captureSentry } from '@/utils/sentryHelper';

/**
 * Minimal case progress type for UI state.
 * Derived from TypedSession but only includes fields needed for context.
 */
export interface CaseProgress {
  id?: string;
  caseId: string;
  completed: boolean;
  currentSlideId: string;
  visitedSlideIds: string[];
  celebrationShown?: boolean;
}

/**
 * Transformed answer type for context usage.
 * This is a UI-friendly format transformed from AnswerFieldsFragment.
 * Note: This is intentionally different from the generated GraphQL type
 * because it uses semantic field names (answerId instead of answerIndexes).
 */
export interface Answer {
  id: string;
  caseProgressId: string;
  slideId: string;
  userId: string;
  answerId: string;
  createdAt: string;
  caseId: string;
}

/**
 * User profile derived from profile data.
 * Minimal type for display purposes.
 */
interface UserProfile {
  full_name?: string;
  email?: string;
  username?: string;
}

interface MasterclassContextType {
  selectedCase: string | null;
  setSelectedCase: (caseKey: string | null) => void;
  hasSeenIntro: boolean;
  setHasSeenIntro: (seen: boolean) => void;
  updateCaseProgress: (
    masterclassId: string,
    caseId: string,
    slideId: string,
    existingVisitedSlideIds?: string[]
  ) => Promise<void>;
  markCaseCompleted: (
    masterclassId: string,
    caseId: string,
    existingProgress?: { currentSlideId?: string; visitedSlideIds?: string[] }
  ) => Promise<void>;
  markCelebrationShown: (masterclassId: string, caseId: string) => Promise<void>;
  showIntroVideo: boolean;
  setShowIntroVideo: (show: boolean) => void;
  showConfirmationVideo: boolean;
  setShowConfirmationVideo: (show: boolean) => void;
  showCompletionVideo: boolean;
  setShowCompletionVideo: (show: boolean) => void;
  showLoadingAnimation: boolean;
  setShowLoadingAnimation: (loading: boolean) => void;
  userProfile: UserProfile | null;
  answers: Answer[];
  recordAnswer: (
    masterclassId: string,
    caseId: string,
    slideId: string,
    answerId: string
  ) => Promise<void>;
  getAnswersForSlide: (caseId: string, slideId: string) => Answer[];
  answersLoading: boolean;
  /** Creates a new MasterClassUserSession for the given masterclassId and caseId */
  instantiateCaseProgress: (masterclassId: string, caseId: string) => Promise<void>;
  resetCaseProgress: (masterclassId: string, caseId: string) => Promise<void>;
  resetAccreditationUserSlidePosition: (masterclassId: string, caseId: string) => Promise<void>;
  isDemo: boolean;
  /** Currently selected masterclass ID */
  selectedMasterclass: string | null;
  /** Set the selected masterclass */
  setSelectedMasterclass: (masterclassId: string | null) => void;
  /** Client logo URL for the current masterclass (set from case data) */
  clientLogoUrl: string | null;
  /** Set the client logo URL */
  setClientLogoUrl: (url: string | null) => void;
  /** Masterclass title for the current masterclass */
  masterclassTitle: string | null;
  /** Set the masterclass title */
  setMasterclassTitle: (title: string | null) => void;
}

const MasterclassContext = createContext<MasterclassContextType | undefined>(undefined);

export const MasterclassProvider = ({ children }: { children: ReactNode }) => {
  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [hasSeenIntro, setHasSeenIntro] = useState(false);
  const [showIntroVideo, setShowIntroVideo] = useState(false);
  const [showConfirmationVideo, setShowConfirmationVideo] = useState(false);
  const [showCompletionVideo, setShowCompletionVideo] = useState(false);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
  const [selectedMasterclass, setSelectedMasterclass] = useState<string | null>(null);
  const [clientLogoUrl, setClientLogoUrl] = useState<string | null>(null);
  const [masterclassTitle, setMasterclassTitle] = useState<string | null>(null);

  // Apollo Client for direct queries
  const apolloClient = useApolloClient();

  // React Query hooks
  const { data: user } = useAuthUser();
  const userId = user?.id || null;

  const { data: profileData } = useProfile(userId);
  // Lazy-load answers: only fetch when user is inside a masterclass (selectedMasterclass is set)
  const { data: answersData, isLoading: answersLoading } = useAnswers(userId, selectedMasterclass);

  const upsertProgressMutation = useUpsertCaseProgress();
  const insertAnswerMutation = useInsertAnswer();
  const deleteAnswersMutation = useDeleteAnswers();
  const resetProgressMutation = useResetCaseProgress();
  const upsertStatsMutation = useUpsertCaseStatistics();

  // Track which masterclass sessions have been created to prevent duplicate creation
  const createdSessionsRef = useRef<Set<string>>(new Set());

  // Reset confirmation video state on mount
  useEffect(() => {
    setShowConfirmationVideo(false);
  }, []);

  // Set Sentry user context
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    }
  }, [user]);

  // Build user profile
  const userProfile = useMemo<UserProfile | null>(() => {
    if (!profileData) return null;
    return {
      full_name: profileData.full_name ?? undefined,
      email: profileData.email ?? undefined,
      username: profileData.username ?? undefined,
    };
  }, [profileData]);

  // Build answers array
  const answers = useMemo<Answer[]>(() => {
    return answersData || [];
  }, [answersData]);

  /**
   * Update case progress with a new slide visit.
   * Caller should pass existingVisitedSlideIds from their local state/query.
   */
  const updateCaseProgress = useCallback(
    async (
      masterclassId: string,
      caseId: string,
      slideId: string,
      existingVisitedSlideIds?: string[]
    ) => {
      if (!userId) return;

      const visitedSlideIds = [...(existingVisitedSlideIds || [])];

      // Add current slide if not already visited
      if (!visitedSlideIds.includes(slideId)) {
        visitedSlideIds.push(slideId);
      }

      await upsertProgressMutation.mutateAsync({
        userId,
        masterclassId,
        caseId,
        updates: {
          current_slide_id: slideId,
          visited_slide_ids: visitedSlideIds,
        },
      });
    },
    [userId]
  );

  /**
   * Mark a case as completed.
   * Caller should pass existing progress from their local state/query.
   */
  const markCaseCompleted = useCallback(
    async (
      masterclassId: string,
      caseId: string,
      existingProgress?: { currentSlideId?: string; visitedSlideIds?: string[] }
    ) => {
      if (!userId) return;

      // Update session completion status
      await upsertProgressMutation.mutateAsync({
        userId,
        masterclassId,
        caseId,
        updates: {
          completed: true,
          current_slide_id: existingProgress?.currentSlideId,
          visited_slide_ids: existingProgress?.visitedSlideIds,
          celebration_shown: false,
        },
      });

      // Increment completions counter in statistics (for certification unlock)
      await upsertStatsMutation.mutateAsync({
        userId,
        masterclassId,
        caseId,
        updates: {
          completions: 1,
        },
      });
    },
    [userId, upsertProgressMutation, upsertStatsMutation]
  );

  const markCelebrationShown = useCallback(
    async (masterclassId: string, caseId: string) => {
      if (!userId) return;

      await upsertProgressMutation.mutateAsync({
        userId,
        masterclassId,
        caseId,
        updates: {
          celebration_shown: true,
        },
      });
    },
    [userId]
  );

  const instantiateCaseProgress = useCallback(
    async (masterclassId: string, caseId: string) => {
      // Check if already created (via ref to prevent infinite loop)
      const sessionKey = `${masterclassId}:${caseId}`;
      if (!userId || createdSessionsRef.current.has(sessionKey)) {
        return;
      }

      // Mark as created BEFORE the async call to prevent race conditions
      createdSessionsRef.current.add(sessionKey);

      try {
        await upsertProgressMutation.mutateAsync({
          userId,
          masterclassId,
          caseId,
          updates: {
            current_slide_id: '',
            visited_slide_ids: [],
            completed: false,
          },
        });
      } catch (error) {
        // Remove from set on error so it can be retried
        createdSessionsRef.current.delete(sessionKey);
        throw error;
      }
    },
    [userId]
  );

  const resetCaseProgress = useCallback(
    async (masterclassId: string, caseId: string) => {
      if (!userId) {
        Sentry.captureMessage('No user found when resetting case progress', {
          tags: {
            component: 'MasterclassContext',
            operation: 'resetCaseProgress',
          },
        });
        return;
      }

      // Backend mutation handles atomic reset of session AND deletion of answers
      await resetProgressMutation.mutateAsync({ masterclassId, caseId });
    },
    [userId]
  );

  const resetAccreditationUserSlidePosition = useCallback(
    async (masterclassId: string, caseId: string) => {
      if (!userId) {
        Sentry.captureMessage('No user found when resetting slide position', {
          tags: {
            component: 'MasterclassContext',
            operation: 'resetAccreditationUserSlidePosition',
          },
        });
        return;
      }

      await upsertProgressMutation.mutateAsync({
        masterclassId,
        userId,
        caseId,
        updates: {
          current_slide_id: '',
          visited_slide_ids: [],
          celebration_shown: false,
        },
      });
    },
    [userId]
  );

  const recordAnswer = useCallback(
    async (masterclassId: string, caseId: string, slideId: string, answerId: string) => {
      if (!userId) return;

      // Get case progress ID from Apollo using masterclassId AND caseId
      try {
        const { data: sessionData } = await apolloClient.query({
          query: GetMasterClassUserSessionDocument,
          variables: {
            where: {
              // Use individual field filters instead of compound unique
              userId: { equals: userId },
              masterClassId: { equals: masterclassId },
              caseId: { equals: caseId },
            },
          },
          fetchPolicy: 'network-only',
        });

        const existingProgress = sessionData?.getMasterClassUserSession;

        if (!existingProgress) {
          console.error('Error: No case progress found for user, masterclass, and case');
          captureSentry({
            title: 'No case progress found when recording answer',
            detail: { masterclassId, caseId, slideId, answerId, userId },
          });
          return;
        }

        await insertAnswerMutation.mutateAsync({
          caseProgressId: existingProgress.id,
          slideId,
          userId,
          answerId,
          caseId,
          masterclassId,
        });
      } catch (error) {
        console.error('Error recording answer:', error);
        captureSentry({
          title: 'Failed to record answer',
          error,
          detail: { masterclassId, caseId, slideId, answerId, userId },
        });
      }
    },
    [userId, apolloClient]
  );

  const getAnswersForSlide = useCallback(
    (caseId: string, slideId: string): Answer[] => {
      const caseAnswers = answers.filter((answer) => answer.caseId === caseId);
      return caseAnswers.filter((answer) => answer.slideId === slideId);
    },
    [answers]
  );

  const isDemo = profileData?.username?.startsWith('demo') || false;

  /**
   * Get progress metrics for a specific masterclass.
   * Filters cases by masterclassId and calculates completion stats.
   */
  return (
    <MasterclassContext.Provider
      value={{
        selectedCase,
        setSelectedCase,
        hasSeenIntro,
        setHasSeenIntro,
        updateCaseProgress,
        markCaseCompleted,
        markCelebrationShown,
        showIntroVideo,
        setShowIntroVideo,
        showConfirmationVideo,
        setShowConfirmationVideo,
        showCompletionVideo,
        setShowCompletionVideo,
        showLoadingAnimation,
        setShowLoadingAnimation,
        userProfile,
        answers,
        recordAnswer,
        getAnswersForSlide,
        answersLoading,
        instantiateCaseProgress,
        resetCaseProgress,
        resetAccreditationUserSlidePosition,
        isDemo,
        selectedMasterclass,
        setSelectedMasterclass,
        clientLogoUrl,
        setClientLogoUrl,
        masterclassTitle,
        setMasterclassTitle,
      }}
    >
      {children}
    </MasterclassContext.Provider>
  );
};

export const useMasterclass = () => {
  const context = useContext(MasterclassContext);
  if (!context) {
    throw new Error('useMasterclass must be used within MasterclassProvider');
  }
  return context;
};
